import { Component, HostListener, Input, OnChanges } from '@angular/core';
import { Router } from '@angular/router';

import { PortalApiService } from '../../portal-api.service';
import { PortalOverlayService } from '../portal-overlay.service';
import { sectionMeta, visibleSections, sectionActionText, PortalSection } from '../portal-sections';

// The centre "section switcher" pill in the topbar: shows the current section
// and opens a dropdown of the other sections the customer can access on this
// project. Used on every section screen.
@Component({
	selector: 'portal-section-nav',
	template: `
		<button class="sec-nav-pill" (click)="toggle($event)" [class.open]="open">
			<span class="flex-weight-1">{{ currentLabel }}</span>
			<i class="wq wq-chevron-down sec-nav-chevron"></i>
		</button>
		<div class="sec-nav-dropdown" [class.open]="open" (click)="$event.stopPropagation()">
			<button class="sec-nav-item" *ngFor="let s of items"
				[class.sec-nav-current]="s.key === current" (click)="go(s.key)">
				<i [class]="s.icon"></i>
				<span>{{ s.label }}</span>
					<span class="sec-nav-badge" *ngIf="actionText(s.key)"><i class="wq wq-alert"></i>{{ actionText(s.key) }}</span>
			</button>
		</div>
	`
})
export class SectionNavComponent implements OnChanges {

	@Input() projectId!: string;
	@Input() current!: string;
	@Input() sections: string[] = [];

	actions: { [key: string]: number } = {};

	constructor(private router: Router, private api: PortalApiService, private overlay: PortalOverlayService) {}

	// Only one dropdown / panel may be open portal-wide.
	get open(): boolean { return this.overlay.isOpen(this); }

	// ngOnChanges, not ngOnInit: the page components stay mounted when only the
	// route's :id changes, so the badges have to follow the new projectId (same
	// reason project-totals and project-footer do this).
	ngOnChanges(): void {
		this.actions = {};
		if (!this.projectId) return;
		this.api.project.actions(this.projectId, (res: any) => {
			this.actions = res?.actions || {};
		}, () => { /* badges are non-critical — ignore load errors */ });
	}

	get items(): PortalSection[] {
		const keys = this.sections?.length ? visibleSections(this.sections) : [this.current];
		return keys.map(k => sectionMeta(k));
	}

	get currentLabel(): string { return sectionMeta(this.current).label; }

	// "Needs your attention" badge label for a section item (shared with the tiles).
	actionText(key: string): string {
		return sectionActionText(key, this.actions?.[key] || 0);
	}

	toggle(event: MouseEvent): void {
		event.stopPropagation();
		this.overlay.toggle(this);
	}

	@HostListener('document:click')
	close(): void { this.overlay.close(this); }

	go(key: string): void {
		this.overlay.close(this);
		if (key !== this.current) this.router.navigate(['/project', this.projectId, key]);
	}
}
